import { getProtocolConfig } from "@silo/config";
import {
  CollateralMerklReward,
  DebtMerklReward,
  DepositMode,
  DisplayMarket,
  makePercent,
  MarketsByProtocol,
  Percent,
  ProtectedMerklReward,
  ProtocolKey,
} from "@silo/types";
import { getDisplayCollateralYields } from "@silo/utils";
import { Array, BigInt, Order, pipe } from "effect";

interface GetDepositTotalAprParams {
  collateralBaseApr: Percent;
  collateralRewardsApr: Percent;
  collateralMerklRewards: CollateralMerklReward[];
  protectedRewardsApr: Percent;
  protectedMerklRewards: ProtectedMerklReward[];
  depositMode: DepositMode;
  revenueApr: Percent;
}

export function getDepositTotalApr({
  collateralBaseApr,
  collateralRewardsApr,
  collateralMerklRewards,
  protectedRewardsApr,
  protectedMerklRewards,
  depositMode,
  revenueApr,
}: GetDepositTotalAprParams): Percent {
  const displayCollateralYields = getDisplayCollateralYields(depositMode);

  if (displayCollateralYields) {
    const collateralTotalApr = pipe(
      [
        collateralBaseApr,
        collateralRewardsApr,
        ...collateralMerklRewards.map((reward) => reward.apr),
      ],
      BigInt.sumAll,
      makePercent
    );

    return collateralTotalApr;
  } else {
    const protectedTotalApr = pipe(
      [
        protectedRewardsApr,
        revenueApr,
        ...protectedMerklRewards.map((reward) => reward.apr),
      ],
      BigInt.sumAll,
      makePercent
    );

    return protectedTotalApr;
  }
}

interface GetDebtTotalAprParams {
  debtBaseApr: Percent;
  debtRewardsApr: Percent;
  debtMerklRewards: DebtMerklReward[];
}

export function getDebtTotalApr({
  debtBaseApr,
  debtRewardsApr,
  debtMerklRewards,
}: GetDebtTotalAprParams): Percent {
  const totalDebtRewardsApr = pipe(
    [debtRewardsApr, ...debtMerklRewards.map((reward) => reward.apr)],
    BigInt.sumAll,
    makePercent
  );

  const debtTotalApr = pipe(debtBaseApr - totalDebtRewardsApr, makePercent);

  return debtTotalApr;
}

export function sortMarketsByDefaultOrder(
  marketsByProtocol: MarketsByProtocol
): MarketsByProtocol {
  const sortedMarketsByProtocol: MarketsByProtocol = marketsByProtocol.map(
    ({ protocolKey, markets, tvlUsd }) => {
      const sortedMarkets = pipe(
        markets,
        Array.sort(byDefaultOrder(protocolKey))
      );

      return { protocolKey, markets: sortedMarkets, tvlUsd };
    }
  );

  return sortedMarketsByProtocol;
}

function byDefaultOrder(protocolKey: ProtocolKey): Order.Order<DisplayMarket> {
  const { pinnedMarkets } = getProtocolConfig(protocolKey);

  return (a, b): -1 | 0 | 1 => {
    const pinnedIndexA = pinnedMarkets.findIndex(
      (marketAddress) => marketAddress === a.marketAddress
    );

    const pinnedIndexB = pinnedMarkets.findIndex(
      (marketAddress) => marketAddress === b.marketAddress
    );

    if (pinnedIndexA >= 0 && pinnedIndexB >= 0) {
      return pinnedIndexA < pinnedIndexB ? -1 : 1;
    }

    if (pinnedIndexA >= 0) {
      return -1;
    }

    if (pinnedIndexB >= 0) {
      return 1;
    }

    return a.liquidityUsd > b.liquidityUsd ? -1 : 1;
  };
}
